import { useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import type { AudioLevel } from '../../types';

interface AudioLevelMeterProps {
  level: AudioLevel;
  width?: number;
  height?: number;
  showPeak?: boolean;
  segments?: number;
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

export function AudioLevelMeter({
  level,
  width = 20,
  height = 200,
  showPeak = true,
  segments = 30,
  orientation = 'vertical',
  className,
}: AudioLevelMeterProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const peakHoldRef = useRef(0);
  const peakDecayRef = useRef(0);

  const getColorForLevel = useCallback(
    (value: number): string => {
      if (value < 0.6) {
        // Green range
        const t = value / 0.6;
        return `rgb(0, ${Math.round(208 * t + 40)}, ${Math.round(132 * t + 40)})`;
      } else if (value < 0.85) {
        // Yellow range
        const t = (value - 0.6) / 0.25;
        return `rgb(${Math.round(255 * t)}, ${Math.round(208 - 53 * t)}, 0)`;
      } else {
        // Red range
        const t = (value - 0.85) / 0.15;
        return `rgb(${Math.round(255 - 25 * t)}, ${Math.round(Math.max(0, 155 - 155 * t))}, ${Math.round(Math.max(0, 102 * (1 - t)))})`;
      }
    },
    []
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const isHorizontal = orientation === 'horizontal';
    const canvasW = isHorizontal ? height : width;
    const canvasH = isHorizontal ? width : height;

    canvas.width = canvasW * dpr;
    canvas.height = canvasH * dpr;
    ctx.scale(dpr, dpr);

    // Background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.fillRect(0, 0, canvasW, canvasH);

    // Update peak hold
    const avgLevel = (level.left + level.right) / 2;
    if (avgLevel > peakHoldRef.current) {
      peakHoldRef.current = avgLevel;
      peakDecayRef.current = 0;
    } else {
      peakDecayRef.current += 1;
      if (peakDecayRef.current > 60) {
        peakHoldRef.current = Math.max(0, peakHoldRef.current - 0.005);
      }
    }

    const segmentGap = 2;
    const segmentSize = isHorizontal
      ? (canvasW - (segments - 1) * segmentGap) / segments
      : (canvasH - (segments - 1) * segmentGap) / segments;

    const activeSegments = Math.floor(avgLevel * segments);
    const peakSegment = Math.floor(peakHoldRef.current * segments);

    for (let i = 0; i < segments; i++) {
      const isActive = i < activeSegments;
      const isPeak = showPeak && i === peakSegment && i > activeSegments;
      const segmentLevel = i / segments;

      let x: number, y: number, w: number, h: number;

      if (isHorizontal) {
        x = i * (segmentSize + segmentGap);
        y = 2;
        w = segmentSize;
        h = canvasH - 4;
      } else {
        // Vertical: segments go from bottom to top
        const segmentIndex = segments - 1 - i;
        x = 2;
        y = segmentIndex * (segmentSize + segmentGap);
        w = canvasW - 4;
        h = segmentSize;
      }

      if (isActive) {
        ctx.fillStyle = getColorForLevel(segmentLevel);
        ctx.shadowColor = getColorForLevel(segmentLevel);
        ctx.shadowBlur = 4;
      } else if (isPeak) {
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = '#ffffff';
        ctx.shadowBlur = 6;
      } else {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
        ctx.shadowBlur = 0;
      }

      const radius = 2;
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + w - radius, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
      ctx.lineTo(x + w, y + h - radius);
      ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
      ctx.lineTo(x + radius, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.fill();

      ctx.shadowBlur = 0;
    }

    // Draw dB markers
    if (!isHorizontal && canvasW > 15) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.font = '8px monospace';
      ctx.textAlign = 'left';

      const markers = [
        { level: 0, label: '-inf' },
        { level: 0.5, label: '-12' },
        { level: 0.75, label: '-6' },
        { level: 1, label: '0' },
      ];

      markers.forEach(({ level: mLevel, label }) => {
        const y = canvasH - mLevel * canvasH;
        ctx.fillText(label, canvasW + 2, y + 3);
      });
    }

    requestAnimationFrame(draw);
  }, [level, width, height, segments, orientation, showPeak, getColorForLevel]);

  useEffect(() => {
    const raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [draw]);

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: orientation === 'vertical' ? width : height,
          height: orientation === 'vertical' ? height : width,
          borderRadius: '8px',
        }}
        className="bg-black/30"
      />
    </motion.div>
  );
}

// Compact level indicator with just a bar
interface CompactLevelProps {
  level: number;
  className?: string;
}

export function CompactLevel({ level, className }: CompactLevelProps) {
  const getColor = (value: number): string => {
    if (value < 0.6) return 'bg-success';
    if (value < 0.85) return 'bg-warning';
    return 'bg-error';
  };

  return (
    <div className={`h-1.5 w-full bg-white/10 rounded-full overflow-hidden ${className}`}>
      <motion.div
        className={`h-full rounded-full ${getColor(level)}`}
        style={{ width: `${Math.min(level * 100, 100)}%` }}
        initial={false}
        animate={{ width: `${Math.min(level * 100, 100)}%` }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      />
    </div>
  );
}
